/**
 * The page's side of the restore bridge: page -> browser plug-in -> daemon -> vsrecovery.
 *
 * Every message here is the one defined by the Local Execution Bridge session's document
 * "Restore runs: what Orchestrate sends and receives" (revised 15 Oct 2026). Nothing is
 * invented: the envelope, the payload types this uses (restoreRun, restoreStop, restoreSync,
 * auditSync, restoreCapabilities, and for real restores restorePlan and restoreStart - and no
 * others), the three kinds that come back (restore,
 * restoreLink, relayError) and the frames inside `restore` (restoreReply, restoreEvent,
 * restoreStatus, auditEntry).
 * Beside them, getVersion (answered by `version`), from the extension protocol document, section
 * 4: the setup screen's way of telling whether the plug-in is in this browser at all.
 *
 * `sessionToken` is the sign-in pass Orchestrate's back end signs for the signed-in engineer
 * (backend/src/lib/session-pass.js). It is sent exactly as received: this never builds or
 * alters it.
 *
 * What the document says and this keeps:
 *   - Messages go with window.postMessage(msg, location.origin), and only messages from this
 *     origin, marked __vaultsentinel with dir "to-page", are listened to.
 *   - A `restore` envelope carries no requestId: events arrive because a run is going, not
 *     because this page asked. A reply is matched by the requestId INSIDE its frame.
 *   - Any payload field not listed is refused by the plug-in, so nothing extra is sent - there
 *     is no stateDir or contract here; the daemon adds what it needs.
 *   - `secrets` exists ONLY on restoreStart (section 3a): the values the engineer typed, beside
 *     the signed job, never sent to Orchestrate's back end and never stored. The caller clears
 *     its own copy as soon as the message is sent (Andrew, 15 Oct 2026).
 *   - `restoreLink` closed does not mean a run stopped.
 */
export const PROTOCOL_VERSION = '1.0'

export interface AgentEvent { v: 1; run_id: string; seq: number; ts: string; type: string; [field: string]: unknown }
export type DaemonState = 'queued' | 'started' | 'ended' | 'lost' | 'adopted'
export interface RestoreReply { type: 'restoreReply'; requestId: string; op: string; ok: boolean; reason?: string; runId?: string; state?: 'started' | 'queued'; position?: number; data?: unknown }
export interface RestoreEventFrame { type: 'restoreEvent'; runId: string; replay?: boolean; event: AgentEvent }
export interface RestoreStatusFrame { type: 'restoreStatus'; runId: string; status: { runId: string; state: DaemonState; command?: string; position?: number; code?: string; otherRuns?: string[]; exitCode?: number } }
/** One record of the daemon's audit journal, as it wrote it. */
export interface AuditEntry { seq: number; record: { recordId: string; outcome?: string; restoreRunId?: string; [field: string]: unknown } }
export interface AuditEntryFrame { type: 'auditEntry'; entry: AuditEntry }
/** `data` of the reply to auditSync. */
export interface AuditSyncData { entries?: AuditEntry[]; more?: boolean; lastSeq?: number; journal?: boolean }
export type Frame = RestoreReply | RestoreEventFrame | RestoreStatusFrame | AuditEntryFrame

export type BridgeMessage =
    | { kind: 'restore'; frame: Frame }
    | { kind: 'restoreLink'; state: 'open' | 'closed'; message?: string; transport?: string }
    // From plug-in 1.7.0 a relayError about reaching the daemon carries a fixed `code` (the setup
    // screen document, section 3). Act on the code; the message is detail only.
    | { kind: 'relayError'; requestId?: string; message?: string; code?: string }

export type RestorePayload =
    | { type: 'restoreRun'; runId: string; command: string; options: Record<string, string | number | boolean> }
    | { type: 'restoreStop'; runId: string; second?: boolean }
    | { type: 'restoreSync'; cursors: Array<{ runId: string; afterSeq: number }> }
    | { type: 'auditSync'; afterSeq: number }
    | { type: 'restoreCapabilities' }
    // A real restore (section 3a): the signed job and approval as the back end returned them,
    // unchanged. restorePlan has no secrets; restoreStart carries exactly the job's secret names.
    | { type: 'restorePlan'; approvalId: string; job: string; capability: string }
    | { type: 'restoreStart'; approvalId: string; job: string; capability: string; secrets: Record<string, string> }

export class BridgeError extends Error {
    /** `reason`: the daemon's reason on a refusal, or the relayError's `code` when it has one. */
    constructor(public code: 'no_plugin' | 'relay_error' | 'refused', message: string, public reason?: string) { super(message) }
}

/** The plug-in's answer to getVersion (the extension protocol document, section 4). */
export interface PluginVersion { extensionVersion: string; protocolVersion: string | null }
/** How long getVersion waits: no answer in this time means the plug-in is not in this browser. */
export const VERSION_TIMEOUT_MS = 2000

/** How long to wait for a reply before deciding the plug-in is not there. */
export const REPLY_TIMEOUT_MS = 8000

export interface RestoreBridge {
    /** Send a request and wait for its restoreReply. Rejects with BridgeError when refused, relayed as an error, or unanswered. */
    request(payload: RestorePayload, sessionToken: string, timeoutMs?: number): Promise<RestoreReply>
    /** Everything that arrives from the plug-in, replies included. Returns the unsubscribe. */
    subscribe(listener: (message: BridgeMessage) => void): () => void
    /** The plug-in's version, or null when nothing answers within VERSION_TIMEOUT_MS (not installed). */
    getVersion(timeoutMs?: number): Promise<PluginVersion | null>
    dispose(): void
}

let counter = 0
const newRequestId = () => `orch-${Date.now().toString(36)}-${(counter += 1).toString(36)}`

export function createRestoreBridge(win: Window = window): RestoreBridge {
    const listeners = new Set<(message: BridgeMessage) => void>()
    const versions = new Map<string, (answer: PluginVersion | null) => void>()
    const waiting = new Map<string, { resolve: (reply: RestoreReply) => void; reject: (error: BridgeError) => void; timer: ReturnType<typeof setTimeout> }>()

    const settle = (requestId: string | undefined, act: (entry: { resolve: (reply: RestoreReply) => void; reject: (error: BridgeError) => void }) => void) => {
        if (!requestId) return
        const entry = waiting.get(requestId)
        if (!entry) return
        clearTimeout(entry.timer)
        waiting.delete(requestId)
        act(entry)
    }

    const onMessage = (event: MessageEvent) => {
        // Only this page's own origin, and only the plug-in's messages to the page.
        if (event.origin !== win.location.origin) return
        const data = event.data as { __vaultsentinel?: boolean; dir?: string; kind?: string; frame?: Frame; state?: 'open' | 'closed'; message?: string; transport?: string; requestId?: string; code?: unknown; extensionVersion?: unknown; protocolVersion?: unknown } | null
        if (!data || data.__vaultsentinel !== true || data.dir !== 'to-page') return
        let message: BridgeMessage | null = null
        if (data.kind === 'version') {
            const answer = data.requestId ? versions.get(data.requestId) : undefined
            if (answer && typeof data.extensionVersion === 'string') answer({ extensionVersion: data.extensionVersion, protocolVersion: typeof data.protocolVersion === 'string' ? data.protocolVersion : null })
            return
        }
        if (data.kind === 'restore' && data.frame && typeof data.frame === 'object') {
            message = { kind: 'restore', frame: data.frame }
            if (data.frame.type === 'restoreReply') {
                const reply = data.frame
                settle(reply.requestId, (entry) => (reply.ok ? entry.resolve(reply) : entry.reject(new BridgeError('refused', `The daemon refused: ${reply.reason ?? 'no reason given'}`, reply.reason))))
            }
        } else if (data.kind === 'restoreLink' && (data.state === 'open' || data.state === 'closed')) {
            message = { kind: 'restoreLink', state: data.state, message: data.message, transport: data.transport }
        } else if (data.kind === 'relayError') {
            const code = typeof data.code === 'string' && data.code ? data.code : undefined
            message = { kind: 'relayError', requestId: data.requestId, message: data.message, ...(code ? { code } : {}) }
            settle(data.requestId, (entry) => entry.reject(new BridgeError('relay_error', data.message || 'The plug-in could not reach the daemon.', code)))
        }
        if (message) listeners.forEach((listener) => listener(message!))
    }
    win.addEventListener('message', onMessage)

    return {
        request(payload, sessionToken, timeoutMs = REPLY_TIMEOUT_MS) {
            const requestId = newRequestId()
            return new Promise<RestoreReply>((resolve, reject) => {
                const timer = setTimeout(() => {
                    waiting.delete(requestId)
                    reject(new BridgeError('no_plugin', 'No answer from the browser plug-in. Is it installed, version 1.6.0 or later, and connected to the daemon?'))
                }, timeoutMs)
                waiting.set(requestId, { resolve, reject, timer })
                win.postMessage({ __vaultsentinel: true, dir: 'to-ext', kind: 'restore', requestId, payload: { ...payload, protocolVersion: PROTOCOL_VERSION, sessionToken } }, win.location.origin)
            })
        },
        getVersion(timeoutMs = VERSION_TIMEOUT_MS) {
            const requestId = newRequestId()
            return new Promise<PluginVersion | null>((resolve) => {
                const done = (answer: PluginVersion | null) => { clearTimeout(timer); versions.delete(requestId); resolve(answer) }
                const timer = setTimeout(() => done(null), timeoutMs)
                versions.set(requestId, done)
                win.postMessage({ __vaultsentinel: true, dir: 'to-ext', kind: 'getVersion', requestId }, win.location.origin)
            })
        },
        subscribe(listener) {
            listeners.add(listener)
            return () => { listeners.delete(listener) }
        },
        dispose() {
            win.removeEventListener('message', onMessage)
            waiting.forEach((entry) => clearTimeout(entry.timer))
            waiting.clear()
            versions.forEach((answer) => answer(null))
            listeners.clear()
        },
    }
}
